import React from 'react';
import { Achievement } from '../types';

interface AchievementDetailModalProps {
  achievement: Achievement | null;
  isOpen: boolean;
  onClose: () => void;
}

const AchievementDetailModal: React.FC<AchievementDetailModalProps> = ({ 
  achievement, 
  isOpen, 
  onClose 
}) => {
  if (!isOpen || !achievement) return null;

  const categoryColors: Record<string, string> = {
    quest: 'from-blue-600 to-cyan-600',
    level: 'from-purple-600 to-pink-600',
    streak: 'from-orange-600 to-red-600',
    stat: 'from-green-600 to-emerald-600',
    special: 'from-yellow-600 to-orange-600',
  };

  const categoryLabels: Record<string, string> = {
    quest: 'Quest Achievement',
    level: 'Level Achievement',
    streak: 'Streak Achievement',
    stat: 'Stat Achievement',
    special: 'Special Achievement',
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 backdrop-blur-xl z-[100] animate-fade-in modal-backdrop"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-3 sm:p-4 pointer-events-none">
        <div 
          className="relative max-w-sm sm:max-w-lg w-full pointer-events-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Content Container */}
          <div className={`relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-r-2 border-b-2 ${
            achievement.unlocked ? 'border-blue-500/50 border-r-blue-700/70 border-b-blue-700/70' : 'border-gray-700/60 border-r-gray-800/70 border-b-gray-800/70'
          } rounded-3xl overflow-hidden animate-scale-in`}
            style={{
              boxShadow: achievement.unlocked 
                ? '0 8px 24px rgba(59, 130, 246, 0.6), 0 2px 8px rgba(59, 130, 246, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                : '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-gray-400 hover:text-white text-lg sm:text-xl z-10 transition-colors bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-1.5 sm:p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              ✕
            </button>

            {/* Achievement Icon */}
            <div className="flex justify-center pt-5 sm:pt-6 pb-1.5 sm:pb-2">
              <div className="relative">
                {achievement.image ? (
                  <img 
                    src={achievement.image} 
                    alt={achievement.title}
                    className={`w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-[0_0_30px_rgba(59,130,246,0.8)] animate-float-3d-slow ${
                      achievement.unlocked ? '' : 'grayscale opacity-40'
                    }`}
                  />
                ) : (
                  <div 
                    className={`text-5xl sm:text-6xl drop-shadow-[0_0_30px_rgba(59,130,246,0.8)] animate-float-3d-slow ${
                      achievement.unlocked ? '' : 'grayscale opacity-40'
                    }`}
                  >
                    {achievement.icon}
                  </div>
                )}
                
                {/* Lock/Unlock Indicator */}
                {achievement.unlocked ? (
                  <div className="absolute -bottom-1 -right-1">
                    <div className="text-3xl drop-shadow-[0_0_20px_rgba(34,197,94,0.8)]">✓</div>
                  </div>
                ) : (
                  <div className="absolute -bottom-1 -right-1">
                    <img 
                      src="/icons/icon-locked.png" 
                      alt="Locked"
                      className="w-8 h-8 object-contain"
                      style={{ filter: 'drop-shadow(0 0 20px rgba(107, 114, 128, 0.8))' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Achievement Title */}
            <h2 className={`text-lg sm:text-2xl font-bold font-orbitron text-center mb-1.5 sm:mb-2 px-3 sm:px-4 tracking-wider drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)] ${
              achievement.unlocked ? 'glow-text' : 'text-gray-500'
            }`}>
              {achievement.title}
            </h2>

            {/* Category Badge */}
            <div className="flex justify-center mb-2.5 sm:mb-3">
              <span className={`px-2.5 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-r ${categoryColors[achievement.category]} text-white font-orbitron font-bold text-[10px] sm:text-xs rounded-full border-2 border-white/20 shadow-[0_0_20px_rgba(59,130,246,0.5)]`}>
                {categoryLabels[achievement.category]}
              </span>
            </div>

            {/* Description */}
            <div className="px-3 sm:px-4 mb-3 sm:mb-4">
              <div className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3"
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <p className={`text-[13px] sm:text-sm font-rajdhani font-bold text-center leading-relaxed ${
                  achievement.unlocked ? 'text-white' : 'text-gray-400'
                }`} style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}>
                  {achievement.description}
                </p>
              </div>
            </div>

            {/* Requirement Section */}
            <div className="px-3 sm:px-4 mb-3 sm:mb-4">
              <div className="bg-gradient-to-b from-blue-950/40 via-slate-950/30 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 rounded-2xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="text-center mb-1.5 sm:mb-2">
                  <span className="font-orbitron text-system-blue text-[10px] sm:text-xs tracking-wider uppercase">
                    ［ REQUIREMENT ］
                  </span>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl mb-1">{achievement.unlocked ? '✓' : '🎯'}</div>
                  <p className={`text-[13px] sm:text-sm font-rajdhani font-semibold ${
                    achievement.unlocked ? 'text-green-400' : 'text-yellow-400'
                  }`}>
                    {achievement.category === 'quest' && `Complete ${achievement.requirement} quests`}
                    {achievement.category === 'level' && `Reach level ${achievement.requirement}`}
                    {achievement.category === 'streak' && `Maintain ${achievement.requirement}-day streak`}
                    {achievement.category === 'stat' && `Reach ${achievement.requirement} total stats`}
                    {achievement.category === 'special' && `Complete special requirement`}
                  </p>
                </div>
              </div>
            </div>

            {/* Unlock Status */}
            <div className="px-3 sm:px-4 pb-3 sm:pb-4">
              {achievement.unlocked && achievement.unlockedAt ? (
                <div className="bg-gradient-to-b from-green-950/40 via-emerald-950/30 to-green-950/40 backdrop-blur-sm border-t-2 border-l-2 border-green-500/50 border-r-2 border-b-2 border-r-green-700/70 border-b-green-700/70 rounded-2xl p-3 sm:p-4 text-center"
                  style={{
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-lg sm:text-xl">🏆</span>
                    <span className="text-base sm:text-lg font-orbitron font-bold text-green-400">
                      UNLOCKED
                    </span>
                    <span className="text-lg sm:text-xl">🏆</span>
                  </div>
                  <p className="text-[11px] sm:text-xs font-rajdhani text-green-300">
                    {new Date(achievement.unlockedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              ) : (
                <div className="bg-gradient-to-b from-gray-950/40 via-slate-950/30 to-gray-950/40 backdrop-blur-sm border-t-2 border-l-2 border-gray-600/50 border-r-2 border-b-2 border-r-gray-800/70 border-b-gray-800/70 rounded-2xl p-3 sm:p-4 text-center"
                  style={{
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <img 
                      src="/icons/icon-locked.png" 
                      alt="Locked"
                      className="w-5 h-5 object-contain"
                      style={{ filter: 'drop-shadow(0 0 8px rgba(107, 114, 128, 0.6))' }}
                    />
                    <span className="text-base sm:text-lg font-orbitron font-bold text-gray-400">
                      LOCKED
                    </span>
                  </div>
                  <p className="text-[11px] sm:text-xs font-rajdhani text-gray-500">
                    Complete the requirement to unlock this achievement
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Decorative corners */}
          <div className="absolute -top-2 -left-2 w-12 h-12 border-t-4 border-l-4 border-system-blue"></div>
          <div className="absolute -top-2 -right-2 w-12 h-12 border-t-4 border-r-4 border-system-blue"></div>
          <div className="absolute -bottom-2 -left-2 w-12 h-12 border-b-4 border-l-4 border-system-blue"></div>
          <div className="absolute -bottom-2 -right-2 w-12 h-12 border-b-4 border-r-4 border-system-blue"></div>
        </div>
      </div>
    </>
  );
};

export default AchievementDetailModal;

